// The chart math behind Progress's estimated-max lines (spec D15: charts
// are hand-built SVG — no chart library). Pure — pinned by chart.test.js;
// LiftChart only draws what `lineChart` lays out.
//
// Days are calendar days, "YYYY-MM-DD", already in the person's zone (the
// API's trend dates are): the x axis counts whole days between them with
// no clock and no zone, so a DST change can't make a day 23 or 25 hours.

const DAY_MS = 86_400_000;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** "YYYY-MM-DD" → whole days since 1970-01-01; NaN for anything else (or a day that doesn't exist). */
export function dayNumber(day) {
  const match = DAY_RE.exec(typeof day === "string" ? day : "");
  if (!match) return NaN;
  const [year, month, date] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const at = new Date(Date.UTC(year, month, date));
  // Date.UTC rolls Feb 31 over into March: that isn't a day
  if (at.getUTCFullYear() !== year || at.getUTCMonth() !== month || at.getUTCDate() !== date) return NaN;
  return Math.round(at.getTime() / DAY_MS);
}

/** Whole days since 1970-01-01 → "YYYY-MM-DD". */
export function dayString(n) {
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

/** The first day of the `weeks` ending on `today` ("YYYY-MM-DD"); "" for a bad day. */
export function windowStart(today, weeks = 26) {
  const end = dayNumber(today);
  return Number.isNaN(end) ? "" : dayString(end - weeks * 7);
}

// The x axis spans at least this many days, so two sessions a week apart
// don't stretch across the whole plot as if they were months.
const MIN_SPAN_DAYS = 28;
// Room before the first point, as a share of the span: its dot doesn't sit
// on the axis line.
const LEAD_SHARE = 0.04;

/**
 * Where a lift's x axis starts: at its first point (a little before it),
 * not at the 26-week window's start — ten weeks of history drew in the
 * right third of the plot, with dots too close to tap on a phone. Never
 * less than MIN_SPAN_DAYS before `today`, never before the window. `days`
 * are the points' "YYYY-MM-DD"; with none (or a bad `today`) it's the
 * window's start.
 */
export function chartStart(days, today, weeks = 26) {
  const end = dayNumber(today);
  if (Number.isNaN(end)) return "";
  const window = end - weeks * 7;
  const first = Math.min(...(days ?? []).map(dayNumber).filter((n) => !Number.isNaN(n)));
  if (!Number.isFinite(first)) return dayString(window);
  const start = Math.min(first, end - MIN_SPAN_DAYS);
  const lead = Math.max(1, Math.round((end - start) * LEAD_SHARE));
  return dayString(Math.max(window, start - lead));
}

/**
 * A linear map from the domain [d0, d1] onto the range [r0, r1]. An empty
 * domain (one value, or all equal) maps everything to the range's middle.
 */
export function scaleLinear([d0, d1], [r0, r1]) {
  if (d1 === d0) {
    const middle = (r0 + r1) / 2;
    return () => middle;
  }
  const k = (r1 - r0) / (d1 - d0);
  return (value) => r0 + (value - d0) * k;
}

/** A step of 1, 2 or 5 × 10^k that splits `span` into at most about `count` parts. */
export function niceStep(span, count = 4) {
  if (!(span > 0) || !Number.isFinite(span) || !(count >= 1)) return 1;
  const raw = span / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / power;
  // round UP to a nice step, so there are never more than ~count parts
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return nice * power;
}

// Ticks are sums of a step; 0.1 + 0.2 must still read 0.3.
const decimalsOf = (step) => Math.max(0, -Math.floor(Math.log10(step)));
const tidy = (value, decimals) => Number(value.toFixed(decimals));

// Below this share of the value, a spread is drawn as no wider than it.
const MIN_SPAN_SHARE = 0.1;
// Float noise in `value / step` (0.9 + 0.1 is 0.9999999999999999) must not
// add a step at either end.
const EPSILON = 1e-9;

/**
 * Y ticks for values from `min` to `max`: `{min, max, step, ticks}`, the
 * domain widened out to whole steps. A spread narrower than 10% of the
 * value (at least 1) — equal values, one point, or estimates a few tenths
 * apart — is widened to that, around its middle: noise sits mid-axis as
 * the near-flat line it is, rather than climbing the whole plot on ticks
 * finer than the data. Equal and near-equal values get the same axis.
 * Values never cross below 0 when they started at or above it (what the
 * floor takes from below goes on top). Non-numbers give a 0–1 axis.
 */
export function niceTicks(min, max, count = 4) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1, step: 1, ticks: [0, 1] };
  let low = Math.min(min, max);
  let high = Math.max(min, max);
  const middle = (low + high) / 2;
  const minSpan = Math.max(Math.abs(middle) * MIN_SPAN_SHARE, 1);
  if (high - low < minSpan) {
    const floored = low >= 0;
    low = middle - minSpan / 2;
    high = middle + minSpan / 2;
    if (floored && low < 0) {
      high -= low;
      low = 0;
    }
  }
  const step = niceStep(high - low, count);
  const decimals = decimalsOf(step);
  const first = tidy(Math.floor(low / step + EPSILON) * step, decimals);
  const last = tidy(Math.ceil(high / step - EPSILON) * step, decimals);
  const ticks = [];
  for (let i = 0; tidy(first + i * step, decimals) <= last; i += 1) {
    ticks.push(tidy(first + i * step, decimals));
  }
  return { min: first, max: last, step, ticks };
}

// One decimal is finer than a pixel and keeps the path short.
const px = (value) => Math.round(value * 10) / 10;

/** An SVG path through `points` ([{x, y}], in order): "M10,20L30,40". Fewer than two draw no line (""). */
export function linePath(points) {
  if (!Array.isArray(points) || points.length < 2) return "";
  return points.map((point, i) => `${i === 0 ? "M" : "L"}${px(point.x)},${px(point.y)}`).join("");
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * X ticks from `startDay` to `endDay` (inclusive): the first of each month
 * inside the range, `{day, value, label}` — "Aug", and the year in place of
 * "Jan" ("2027") so a turn of the year is said. More than `max` and every
 * other one is kept (every third, …), first included.
 */
export function monthTicks(startDay, endDay, max = 7) {
  const start = dayNumber(startDay);
  const end = dayNumber(endDay);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];
  const from = new Date(start * DAY_MS);
  let year = from.getUTCFullYear();
  let month = from.getUTCMonth() + (from.getUTCDate() === 1 ? 0 : 1);
  const ticks = [];
  for (;;) {
    if (month === 12) {
      month = 0;
      year += 1;
    }
    const value = Math.round(Date.UTC(year, month, 1) / DAY_MS);
    if (value > end) break;
    ticks.push({ day: dayString(value), value, label: month === 0 ? String(year) : MONTHS[month] });
    month += 1;
  }
  const every = Math.max(1, Math.ceil(ticks.length / Math.max(1, max)));
  return ticks.filter((_, i) => i % every === 0);
}

/**
 * X ticks for a span too short for months to say much (fewer than two
 * firsts of the month in it): every Sunday from `startDay` to `endDay`,
 * `{day, value, label}` labelled "Sep 27", thinned to at most `max` (every
 * other one, …, first included).
 */
export function weekTicks(startDay, endDay, max = 5) {
  const start = dayNumber(startDay);
  const end = dayNumber(endDay);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];
  // 1970-01-01 was a Thursday: day n is a Sunday when (n + 4) % 7 === 0
  const sunday = start + ((7 - ((start + 4) % 7)) % 7);
  const ticks = [];
  for (let value = sunday; value <= end; value += 7) {
    const at = new Date(value * DAY_MS);
    ticks.push({ day: dayString(value), value, label: `${MONTHS[at.getUTCMonth()]} ${at.getUTCDate()}` });
  }
  const every = Math.max(1, Math.ceil(ticks.length / Math.max(1, max)));
  return ticks.filter((_, i) => i % every === 0);
}

/**
 * Lay out one lift's line: `trend` ([{date, e1rm, …}], oldest first) over
 * the days `start`…`end` in a `width` × `height` box with `margin` around
 * the plot. The x domain grows to take in a point outside the window rather
 * than drop it. Returns the plot box, the y ticks (`{value, y}`), the month
 * ticks (`{label, x}`), the line's path and the points (`{x, y, point}`,
 * in trend order). A point without a usable day or number is left out.
 */
export function lineChart(trend, { start, end, width, height, margin }) {
  const points = (trend ?? []).filter(
    (point) => !Number.isNaN(dayNumber(point?.date)) && Number.isFinite(point?.e1rm),
  );
  const days = points.map((point) => dayNumber(point.date));
  const startValue = Math.min(dayNumber(start), ...days);
  const endValue = Math.max(dayNumber(end), ...days);
  const plot = {
    left: margin.left,
    right: width - margin.right,
    top: margin.top,
    bottom: height - margin.bottom,
  };
  const values = points.map((point) => point.e1rm);
  const axis = niceTicks(Math.min(...values), Math.max(...values));
  const x = scaleLinear([startValue, endValue], [plot.left, plot.right]);
  const y = scaleLinear([axis.min, axis.max], [plot.bottom, plot.top]);
  const placed = points.map((point, i) => ({ x: x(days[i]), y: y(point.e1rm), point }));
  const [from, to] = [dayString(startValue), dayString(endValue)];
  const months = monthTicks(from, to);
  return {
    plot,
    yTicks: axis.ticks.map((value) => ({ value, y: y(value) })),
    xTicks: (months.length >= 2 ? months : weekTicks(from, to)).map((tick) => ({ ...tick, x: x(tick.value) })),
    path: linePath(placed),
    points: placed,
  };
}

/**
 * Which placed point (`{x}`, as lineChart lays them out) a tap or click at
 * `x` means: the nearest by x, and on a tie the later in the list (the
 * newer session). -1 when there are none or `x` isn't a number.
 *
 * One target for the whole plot, not one per dot: with sessions twice a
 * week, per-dot targets were ~6 px wide on a phone, and Chrome's touch
 * adjustment moved a tap on one dot onto its neighbour's target (measured:
 * a tap on Oct 6 opened Oct 2).
 */
export function nearestPoint(points, x) {
  if (!Number.isFinite(x)) return -1;
  let found = -1;
  let distance = Infinity;
  (points ?? []).forEach((point, i) => {
    const d = Math.abs(point.x - x);
    if (d <= distance) {
      found = i;
      distance = d;
    }
  });
  return found;
}
